import type { LocationQueryRaw, Router } from 'vue-router';
import { track, TrackEventType, TrackSourceType } from '@eureka/shared/src/utils/track';
import { getIsOverseas } from '@eureka/shared/src/utils/site-region';
import { AgentType } from '@/features/drafting/constants/enum';

export const DRAFTING_PRODUCT_LINE = 'eureka_ip_drafting';

export type UpgradeTriggerSource = 'side_nav' | 'agent_home' | 'eureka_home' | 'paywall' | 'direct';
export type UpgradeAgentName =
    | 'home'
    | 'ip_drafting_invention_disclosure'
    | 'ip_drafting_patent_drafting'
    | 'ip_drafting_oar';

export interface UpgradeTrackingContext {
    product_line: string;
    trigger_source: UpgradeTriggerSource;
    agent_name: UpgradeAgentName;
    page_url: string;
}

export interface SubscribeTrackingContext {
    target_plan: string;
    pay_amount?: string | number;
    billing_cycle?: string;
}

export interface RegionalUpgradeText {
    text: string;
    isHtml: boolean;
}

const DEFAULT_UPGRADE_LINK_STYLE = 'color: inherit; text-decoration: underline; font-weight: 600;';

const AGENT_NAME_MAP: Record<AgentType, UpgradeAgentName> = {
    [AgentType.DISCLOSURE]: 'ip_drafting_invention_disclosure',
    [AgentType.PATENT_DRAFT]: 'ip_drafting_patent_drafting',
    [AgentType.PROSECUTION]: 'ip_drafting_oar',
};

function getCurrentPageUrl(): string {
    return window.location.href;
}

function getQueryValue(value: unknown): string | undefined {
    if (Array.isArray(value)) return value[0] ? String(value[0]) : undefined;
    return value == null || value === '' ? undefined : String(value);
}

function normalizeTriggerSource(value: unknown): UpgradeTriggerSource {
    const triggerSource = getQueryValue(value);
    if (
        triggerSource === 'side_nav'
        || triggerSource === 'agent_home'
        || triggerSource === 'eureka_home'
        || triggerSource === 'paywall'
        || triggerSource === 'direct'
    ) {
        return triggerSource;
    }
    return 'direct';
}

function normalizeAgentName(value: unknown): UpgradeAgentName {
    const agentName = getQueryValue(value);
    if (
        agentName === 'ip_drafting_invention_disclosure'
        || agentName === 'ip_drafting_patent_drafting'
        || agentName === 'ip_drafting_oar'
        || agentName === 'home'
    ) {
        return agentName;
    }
    return 'home';
}

export function getUpgradeAgentName(agentType?: AgentType | null): UpgradeAgentName {
    return agentType ? AGENT_NAME_MAP[agentType] || 'home' : 'home';
}

export function createUpgradeTrackingContext(params: {
    trigger_source: UpgradeTriggerSource;
    agent_name?: UpgradeAgentName;
    agent_type?: AgentType | null;
    page_url?: string;
}): UpgradeTrackingContext {
    return {
        product_line: DRAFTING_PRODUCT_LINE,
        trigger_source: params.trigger_source,
        agent_name: params.agent_name || getUpgradeAgentName(params.agent_type),
        page_url: params.page_url || getCurrentPageUrl(),
    };
}

// 跳转升级页时通过 history.state 透传完整上下文，避免污染 URL
export const UPGRADE_STATE_KEY = '__upgradeTrackingContext';

export function buildUpgradeQuery(context: UpgradeTrackingContext): LocationQueryRaw {
    // URL 仅保留 from_trigger，保持简洁；其余字段通过 history.state 透传
    return {
        from_trigger: context.trigger_source,
    };
}

function getUpgradeContextFromState(): Partial<UpgradeTrackingContext> | undefined {
    const state = typeof window !== 'undefined' ? window.history.state : undefined;
    const stateContext = state?.[UPGRADE_STATE_KEY];
    return stateContext && typeof stateContext === 'object' ? stateContext : undefined;
}

// 优先读取来源页透传的 history.state，读不到再回退到 URL query（兼容直接访问/刷新）
export function getUpgradeContextFromQuery(query: Record<string, unknown>): UpgradeTrackingContext {
    const stateContext = getUpgradeContextFromState();
    return {
        product_line: getQueryValue(stateContext?.product_line)
            || getQueryValue(query.product_line)
            || DRAFTING_PRODUCT_LINE,
        trigger_source: normalizeTriggerSource(
            stateContext?.trigger_source ?? query.trigger_source ?? query.from_trigger,
        ),
        agent_name: normalizeAgentName(stateContext?.agent_name ?? query.agent_name),
        page_url: getQueryValue(stateContext?.page_url) || getQueryValue(query.page_url) || getCurrentPageUrl(),
    };
}

export function trackUpgradeTrigger(context: UpgradeTrackingContext): void {
    void track({
        event_type: TrackEventType.EurekaUpgradePage,
        source_type: TrackSourceType.UpgradeTrigger,
        event_detail_str: JSON.stringify(context),
    });
}

export function trackPricingPageView(context: UpgradeTrackingContext, extra?: Record<string, string>): void {
    void track({
        event_type: TrackEventType.EurekaUpgradePage,
        source_type: TrackSourceType.PricingPageView,
        event_detail_str: JSON.stringify({
            ...context,
            ...extra,
        }),
    });
}

export function trackSubscribeClick(context: UpgradeTrackingContext, subscribeContext: SubscribeTrackingContext): void {
    void track({
        event_type: TrackEventType.EurekaUpgradePage,
        source_type: TrackSourceType.ClickSubscribePro,
        event_detail_str: JSON.stringify({
            ...context,
            ...subscribeContext,
        }),
    });
}

export function trackSubmitUpgrade(context: UpgradeTrackingContext, subscribeContext: SubscribeTrackingContext): void {
    void track({
        event_type: TrackEventType.EurekaUpgradePage,
        source_type: TrackSourceType.SubmitUpgrade,
        event_detail_str: JSON.stringify({
            ...context,
            ...subscribeContext,
        }),
    });
}

export function buildUpgradeHref(context: UpgradeTrackingContext): string {
    const hashPath = '#/upgrade';
    const query = new URLSearchParams();
    Object.entries(buildUpgradeQuery(context)).forEach(([key, value]) => {
        if (value != null) query.set(key, String(value));
    });
    return `${window.location.origin}${window.location.pathname}${hashPath}?${query.toString()}`;
}

export function buildRegionalUpgradeText(options: {
    text: string;
    context: UpgradeTrackingContext;
    style?: string;
}): RegionalUpgradeText {
    if (!getIsOverseas()) {
        return {
            text: options.text,
            isHtml: false,
        };
    }

    const style = options.style || DEFAULT_UPGRADE_LINK_STYLE;
    return {
        text: `<a href="${buildUpgradeHref(options.context)}" style="${style}">${options.text}</a>`,
        isHtml: true,
    };
}

export function goToUpgrade(router: Router, context: UpgradeTrackingContext): void {
    trackUpgradeTrigger(context);
    // 完整上下文通过 history.state 透传，URL 仅保留 from_trigger
    void router.push({
        name: 'Upgrade',
        query: buildUpgradeQuery(context),
        state: { [UPGRADE_STATE_KEY]: { ...context } },
    });
}
